import { Link } from "react-router-dom";
import { Target, Eye, ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { AnimatedCounter } from "../components/AnimatedCounter";
import { CTABanner } from "../components/CTABanner";
import { getIcon } from "../data/site";
import { useSite } from "../context/ContentContext";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1739287088753-73a9b8b771bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function About() {
  const { values: VALUES, stats: STATS, whyChoose: WHY_CHOOSE } = useSite();
  return (
    <div data-testid="about-page">
      <Seo title="About Us" description="Learn about Domaleo Enterprise — a trusted multi-sector business partner based in Accra, Ghana with international capabilities." />

      {/* page hero */}
      <section className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1739287088753-73a9b8b771bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative container-px mx-auto max-w-7xl">
          <Reveal>
            <p className="overline text-accent">About Domaleo Enterprise</p>
            <h1 className="mt-3 max-w-3xl text-4xl sm:text-5xl font-bold tracking-tight text-white text-balance">
              A trusted multi-sector partner, built in Ghana for the world
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-slate-200 leading-relaxed">
              Connecting Markets. Delivering Solutions. We empower businesses across Ghana and beyond with reliable, innovative and sustainable services.
            </p>
          </Reveal>
        </div>
      </section>

      {/* overview */}
      <section className="container-px mx-auto max-w-7xl py-20 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <img src={ABOUT_IMG} alt="Domaleo Enterprise team" className="rounded-3xl border border-border object-cover shadow-lg w-full h-[420px]" />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="overline">Company Overview</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">Decades of combined expertise across five sectors</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Domaleo Enterprise is a Ghanaian company headquartered in Accra, providing reliable import and export services, innovative IT solutions, sustainable agricultural solutions, dependable logistics, and efficient general supply services.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              We pride ourselves on professionalism, reliability and customer satisfaction, serving government, corporate organisations, NGOs and international partners with globally competitive standards.
            </p>
            <Link to="/contact" className="mt-7 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105" data-testid="about-cta-btn">
              Work with us <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* mission / vision */}
      <section className="bg-surface py-20 lg:py-24">
        <div className="container-px mx-auto max-w-7xl grid gap-6 md:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-2xl border border-border bg-card p-9">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><Target className="h-6 w-6" /></span>
              <h3 className="mt-5 font-heading text-2xl font-semibold">Our Mission</h3>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                To provide reliable import and export services, innovative IT solutions, sustainable agricultural solutions, dependable logistics, and efficient general supply services that empower businesses across Ghana and beyond.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-2xl border border-border bg-card p-9">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-secondary/10 text-secondary"><Eye className="h-6 w-6" /></span>
              <h3 className="mt-5 font-heading text-2xl font-semibold">Our Vision</h3>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                To be the most trusted and innovative multi-sector business partner in West Africa, recognised globally for excellence, reliability and the lasting value we create for our clients.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* values */}
      <section className="container-px mx-auto max-w-7xl py-20 lg:py-28">
        <SectionHeading overline="Core Values" title="The principles that guide us" align="center" />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={(i % 3) * 0.08}>
              <div className="h-full rounded-2xl border border-border bg-card p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                <span className="font-heading text-3xl font-bold text-accent">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-heading text-lg font-semibold">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* stats strip */}
      <section className="bg-primary py-16">
        <div className="container-px mx-auto max-w-7xl grid grid-cols-2 gap-6 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="text-center">
              <div className="font-heading text-4xl font-bold text-white"><AnimatedCounter value={Number(s.value)} suffix={s.suffix} /></div>
              <p className="mt-2 text-sm text-slate-300">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* why choose recap */}
      <section className="container-px mx-auto max-w-7xl py-20 lg:py-28">
        <SectionHeading overline="Why Clients Choose Domaleo" title="Reliability you can build on" />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {WHY_CHOOSE.map((w, i) => {
            const Icon = getIcon(w.iconName);
            return (
              <Reveal key={w.title} delay={(i % 3) * 0.08}>
                <div className="flex gap-4 rounded-2xl border border-border bg-card p-6">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-secondary"><Icon className="h-5 w-5" /></span>
                  <div>
                    <h3 className="font-heading font-semibold">{w.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{w.text}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
